import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// MUI Components
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// Services
import {
  CategoryEntry,
  createDefaultCategoryEntry,
  defaultCategoryEntry,
} from "../../services/PublishcategoryEntryService";
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Components
import CategoryFormDialog from "./PublishCategoryFormDialog";
import Spinner from "../spinner/Spinner";

// Config
import { appConfig } from "../../config/appConfig";

// Styles
import "./PublishCategorymain.scss";

interface count {
  setCategoryCount: any;
  visible: boolean;
}
const PublishCategorymain: React.FC<count> = ({
  setCategoryCount,
  visible,
}) => {
  // useState
  const [dataList, setDataList] = useState<CategoryEntry[]>([]);
  const [filterList, setFilterList] = useState<CategoryEntry[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CategoryEntry>(defaultCategoryEntry);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [loading, setLoading] = useState(false);

  // useNavigate
  const navigate = useNavigate();

  useEffect(() => {
    const savedData = localStorage.getItem("adminDataList");
    if (savedData) {
      const parsedData: CategoryEntry[] = JSON.parse(savedData);
      // setDataList(parsedData);
      setFilterList(parsedData);

      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filteredData = filterList.filter((item) => item.status === "Pending");
    setDataList(filteredData);
    setCategoryCount(filteredData.length);
  }, [filterList]);
  const resetForm = () => {
    setFormData(createDefaultCategoryEntry(dataList.length + 1)); // auto-increment ID
    setEditingIndex(null);
  };

  useEffect(() => {
    if (editingIndex !== null) {
      const selectedEntry = dataList.find((item) => item.id === editingIndex);
      if (selectedEntry) {
        const icon =
          typeof selectedEntry.icon === "string"
            ? selectedEntry.icon.split(",")
            : Array.isArray(selectedEntry.icon)
              ? selectedEntry.icon
              : [];

        const videoUrls =
          typeof selectedEntry.videoUrls === "string"
            ? selectedEntry.videoUrls.split(",")
            : Array.isArray(selectedEntry.videoUrls)
              ? selectedEntry.videoUrls
              : [];

        setFormData({
          ...selectedEntry,
          icon,
          videoUrls,
        });
      }
    }
  }, [editingIndex, dataList]);

  const fetchData = async () => {
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.saveCategory, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: CategoryEntry[] = await response.json();
      // setDataList(data || []);
      setFilterList(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // handle Approve
  const handleApprove = async (index: number) => {
    if (!window.confirm("Are you sure want to approve & publish this entry?"))
      return;
    await handlePublish(index, "Published", "3");
  };
  // handle Approve
  const handleReject = async (index: number) => {
    if (!window.confirm("Are you sure want to reject this entry?")) return;
    await handlePublish(index, "Rejected", "2");
  };

  // handle Publish
  const handlePublish = async (
    index: number,
    stat: string,
    statcode: string,
  ) => {
    // if (!window.confirm("Are you sure you want to publish this entry?")) return;

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      alert("Invalid entry selected for publishing.");
      return;
    }
    setLoading(true);
    // const formDataToSend = new FormData();
    // formDataToSend.append("id", selectedEntry.id.toString());
    // formDataToSend.append("priority", selectedEntry.priority.toString());
    // formDataToSend.append("name", selectedEntry.name);

    // formDataToSend.append("isActive", selectedEntry.isActive.toString());
    // // formDataToSend.append("status", "Published");
    // formDataToSend.append("uploadBy", selectedEntry.uploadBy);
    // // formDataToSend.append("statusCode", "3");
    // const username = getStorageItemLocal("username") ?? "";
    // formDataToSend.append("approveBy", username);
    // formDataToSend.append("publishBy", username);
    // formDataToSend.append("status", stat); // override
    // formDataToSend.append("statusCode", statcode);
    // formDataToSend.append("icon", selectedEntry.icon.toString());
    // formDataToSend.append("pinIcon", selectedEntry.pinIcon.toString());
    const formDataToSend = new FormData();

    formDataToSend.append("id", selectedEntry?.id?.toString() || "");
    formDataToSend.append(
      "priority",
      selectedEntry?.priority?.toString() || "0",
    );
    formDataToSend.append("name", selectedEntry?.name || "");

    formDataToSend.append(
      "isActive",
      selectedEntry?.isActive != null
        ? selectedEntry.isActive.toString()
        : "false",
    );

    formDataToSend.append("uploadBy", selectedEntry?.uploadBy || "");

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("approveBy", username);
    formDataToSend.append("publishBy", username);

    formDataToSend.append("status", stat);
    formDataToSend.append("statusCode", statcode);

    // ⚠️ THESE are common culprits
    // formDataToSend.append("icon", selectedEntry.icon.toString());
    // formDataToSend.append("pinIcon", selectedEntry.pinIcon.toString());
    formDataToSend.append(
      "icon",
      Array.isArray(selectedEntry?.icon)
        ? selectedEntry.icon.join(",")
        : selectedEntry?.icon || "",
    );

    formDataToSend.append(
      "pinIcon",
      Array.isArray(selectedEntry?.pinIcon)
        ? selectedEntry.pinIcon.join(",")
        : selectedEntry?.pinIcon || "",
    );

    setLoading(true);
    const url = `${appConfig.serviceUrls.saveCategory}/${selectedEntry.id}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        body: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");

      alert(`Data ${stat} successfully`);
      fetchData();
      resetForm();
      setEditingIndex(null);
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Network Error");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;
  return (
    <>
      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            // padding: 2,
            width: "100%",
            // maxHeight: "80vh",
          }}
        >
          {dataList?.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow className="custom-table-head-row">
                  <TableCell>
                    <b>Request ID</b>
                  </TableCell>
                  <TableCell className="nowrap">
                    <b>Category Name</b>
                  </TableCell>

                  <TableCell>
                    <b>Menu Icon</b>
                  </TableCell>
                  <TableCell>
                    <b>Map Pin</b>
                  </TableCell>
                  {/* <TableCell><b>Video</b></TableCell> */}
                  <TableCell>
                    <b>Active / Disable</b>
                  </TableCell>
                  <TableCell>
                    <b>Status</b>
                  </TableCell>
                  <TableCell>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataList
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="nowrap">
                        MAP-{item.id || "\u00A0"}
                      </TableCell>
                      <TableCell>{item.name || "\u00A0"}</TableCell>

                      <TableCell>
                        {typeof item.icon === "string" &&
                        item.icon.trim() !== "" ? (
                          item.icon.split(",").map((img, index) => (
                            <div key={index}>
                              <img
                                loading="lazy"
                                key={index}
                                src={img}
                                alt="Uploaded"
                                width="40"
                                height="40"
                              />
                            </div>
                          ))
                        ) : (
                          <Typography>No icon</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {typeof item.pinIcon === "string" &&
                        item.pinIcon.trim() !== "" ? (
                          item.pinIcon.split(",").map((img, index) => (
                            <Box
                              key={index}
                              component="span"
                              sx={{ display: "inline-block", mr: 1 }}
                            >
                              <img
                                // src={`${API_BASE_URL1}/${img.trim()}`}
                                src={img.trim()}
                                alt={`Icon ${index + 1}`}
                                width={40}
                                height={40}
                                loading="lazy"
                              />
                            </Box>
                          ))
                        ) : (
                          <Typography>No Pin icon</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {item.isActive ? "Active" : "Disable"}
                      </TableCell>
                      {/* <TableCell>{item.status}</TableCell> */}
                      {item.status === "Approved" ? (
                        <TableCell className="approved-text">
                          {item.status}
                        </TableCell>
                      ) : item.status === "Published" ? (
                        <TableCell className="publish-txt">
                          {item.status}
                        </TableCell>
                      ) : item.status === "Pending" ? (
                        <TableCell className="pending-txt">
                          {item.status}
                        </TableCell>
                      ) : (
                        <TableCell>{item.status}</TableCell>
                      )}

                      <TableCell align="left" className="nowrap display-btn">
                        <IconButton
                          className="reject-box"
                          size="small"
                          onClick={() => {
                            handleReject(page * rowsPerPage + index);
                          }}
                        >
                          <Typography className="box-text">Reject</Typography>
                        </IconButton>

                        <IconButton
                          className="approve-box"
                          size="small"
                          onClick={() => {
                            handleApprove(page * rowsPerPage + index);
                          }}
                        >
                          <Typography className="box-text">
                            Approve & Publish
                          </Typography>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            // <></>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
              }}
            >
              <Typography>No records found.</Typography>
            </Box>
          )}

          {/* <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
              }}
            >
              Showing {page * rowsPerPage + 1} to{" "}
              {Math.min(dataList.length, page * rowsPerPage + rowsPerPage)} of{" "}
              {dataList.length} entries
            </Typography>
            <Pagination
              count={Math.ceil(dataList.length / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box> */}
        </TableContainer>
      )}

      <CategoryFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        fetchData={fetchData}
        resetForm={resetForm}
        formData={formData}
        setFormData={setFormData}
        editingIndex={editingIndex}
        setEditingIndex={setEditingIndex}
        setShowForm={setShowForm}
        dataList={dataList}
      />
    </>
  );
};

export default PublishCategorymain;
